const adminPasswordKey = 'maldet-admin-password';

function getAdminPassword() {
  return localStorage.getItem(adminPasswordKey) || '';
}

function galleryTokenKey(slug) {
  return `maldet-gallery-token-${slug}`;
}

async function parseResponse(response, fallbackMessage) {
  const text = await response.text();
  const contentType = response.headers.get('content-type') || '';
  let payload = null;

  if (text && (contentType.includes('application/json') || text.trim().startsWith('{'))) {
    payload = JSON.parse(text);
  }

  if (response.ok && payload) return payload;
  if (response.ok && text.trim().startsWith('<!doctype')) {
    throw new Error('Die API liefert HTML statt JSON. Bitte den Dev-Server komplett neu starten.');
  }
  if (response.ok) return {};

  throw new Error(payload?.error || fallbackMessage);
}

function adminHeaders(extra = {}) {
  return {
    'x-admin-password': getAdminPassword(),
    ...extra
  };
}

function galleryHeaders(slug, extra = {}) {
  const token = localStorage.getItem(galleryTokenKey(slug));
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra
  };
}

export function saveGalleryToken(slug, token) {
  localStorage.setItem(galleryTokenKey(slug), token);
}

export async function fetchProofingGalleries() {
  const response = await fetch('/api/admin/galleries', {
    headers: adminHeaders(),
    credentials: 'same-origin'
  });
  const payload = await parseResponse(response, 'Galerien konnten nicht geladen werden.');
  return payload.galleries || [];
}

export async function createProofingGallery(payload) {
  const response = await fetch('/api/admin/galleries', {
    method: 'POST',
    headers: adminHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'same-origin',
    body: JSON.stringify(payload)
  });
  return parseResponse(response, 'Galerie konnte nicht angelegt werden.');
}

export async function fetchProofingGallery(id) {
  const response = await fetch(`/api/admin/galleries/${id}`, {
    headers: adminHeaders(),
    credentials: 'same-origin'
  });
  return parseResponse(response, 'Galerie konnte nicht geladen werden.');
}

export async function updateProofingGallery(id, payload) {
  const response = await fetch(`/api/admin/galleries/${id}`, {
    method: 'PATCH',
    headers: adminHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'same-origin',
    body: JSON.stringify(payload)
  });
  return parseResponse(response, 'Galerie konnte nicht gespeichert werden.');
}

export async function deleteProofingGallery(id) {
  const response = await fetch(`/api/admin/galleries/${id}`, {
    method: 'DELETE',
    headers: adminHeaders(),
    credentials: 'same-origin'
  });
  return parseResponse(response, 'Galerie konnte nicht gelöscht werden.');
}

export async function updateProofImage(id, patch) {
  const response = await fetch(`/api/admin/proof-images/${id}`, {
    method: 'PATCH',
    headers: adminHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'same-origin',
    body: JSON.stringify(patch)
  });
  return parseResponse(response, 'Bild konnte nicht aktualisiert werden.');
}

export async function deleteProofImage(id) {
  const response = await fetch(`/api/admin/proof-images/${id}`, {
    method: 'DELETE',
    headers: adminHeaders(),
    credentials: 'same-origin'
  });
  return parseResponse(response, 'Bild konnte nicht gelöscht werden.');
}

export async function deleteFinalAsset(id) {
  const response = await fetch(`/api/admin/finals/${id}`, {
    method: 'DELETE',
    headers: adminHeaders(),
    credentials: 'same-origin'
  });
  return parseResponse(response, 'Finaldatei konnte nicht gelöscht werden.');
}

export function uploadGalleryFiles({ galleryId, files, kind, onProgress }) {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('images', file));

    const request = new XMLHttpRequest();
    request.open('POST', `/api/admin/galleries/${galleryId}/${kind}/upload`);
    request.setRequestHeader('x-admin-password', getAdminPassword());
    request.withCredentials = true;

    request.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      onProgress?.(Math.round((event.loaded / event.total) * 100));
    };

    request.onload = () => {
      const text = request.responseText || '{}';
      const payload = text.trim().startsWith('<!doctype')
        ? { error: 'Die API liefert HTML statt JSON. Bitte den Dev-Server komplett neu starten.' }
        : JSON.parse(text);
      if (request.status >= 200 && request.status < 300) resolve(payload);
      else reject(new Error(payload.error || 'Upload fehlgeschlagen.'));
    };

    request.onerror = () => reject(new Error('Upload wurde unterbrochen.'));
    request.send(formData);
  });
}

export async function loginClientGallery(slug, password) {
  const response = await fetch('/api/gallery/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ slug, password })
  });
  const payload = await parseResponse(response, 'Login fehlgeschlagen.');
  if (payload.token) saveGalleryToken(slug, payload.token);
  return payload;
}

export async function fetchClientGallery(slug) {
  const response = await fetch(`/api/gallery/${slug}`, {
    headers: galleryHeaders(slug),
    credentials: 'same-origin'
  });
  return parseResponse(response, 'Galerie konnte nicht geladen werden.');
}

export async function fetchClientImages(slug) {
  const response = await fetch(`/api/gallery/${slug}/images`, {
    headers: galleryHeaders(slug),
    credentials: 'same-origin'
  });
  const payload = await parseResponse(response, 'Bilder konnten nicht geladen werden.');
  return payload.images || [];
}

export async function fetchClientFinals(slug) {
  const response = await fetch(`/api/gallery/${slug}/finals`, {
    headers: galleryHeaders(slug),
    credentials: 'same-origin'
  });
  return parseResponse(response, 'Finale Bilder konnten nicht geladen werden.');
}

export async function updateClientRating(slug, imageId, rating) {
  const response = await fetch(`/api/gallery/${slug}/images/${imageId}/rating`, {
    method: 'PATCH',
    headers: galleryHeaders(slug, { 'Content-Type': 'application/json' }),
    credentials: 'same-origin',
    body: JSON.stringify({ rating })
  });
  return parseResponse(response, 'Bewertung konnte nicht gespeichert werden.');
}
